import { env } from '@authy/lib-client/env'

import type { AccountBootstrap, AccountConnection, AccountSecurity, TotpSetup } from './types'

type RequestOptions = {
    method?: 'GET' | 'POST'
    body?: Record<string, unknown>
}

export class AccountApiError extends Error {
    constructor(
        message: string,
        public readonly code: string | null
    ) {
        super(message)
        this.name = 'AccountApiError'
    }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
    const data = await response.json().catch(() => null)

    if (!response.ok) {
        const message =
            data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
                ? data.message
                : 'Request failed.'

        const code = data && typeof data.code === 'string' ? data.code : null

        throw new AccountApiError(message, code)
    }

    return data as T
}

export const createAccountApi = (tenantSlug: string | undefined) => {
    let baseUrl = `${window.location.origin}/account`

    if (tenantSlug) {
        baseUrl = `${env.VITE_PRODUCT_ENDPOINT}/account/t/${tenantSlug}`
    }

    const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
        const headers = options.body ? { 'Content-Type': 'application/json' } : undefined

        const response = await fetch(`${baseUrl}${path}`, {
            method: options.method ?? 'GET',
            credentials: 'include',
            headers,
            body: options.body ? JSON.stringify(options.body) : undefined,
        })

        return parseResponse<T>(response)
    }

    return {
        baseUrl,
        getBootstrap: () => request<AccountBootstrap>('/api/bootstrap'),
        getSecurity: () => request<AccountSecurity>('/api/security'),
        checkAuthentication: () => request<{ success: true }>('/api/authentication'),
        startConnection: (provider: AccountConnection['provider']) =>
            request<{ authorizationUrl: string }>(`/api/connections/${provider}`, { method: 'POST' }),
        changePassword: (body: { newPassword: string }) =>
            request<{ success: true }>('/api/password/change', { method: 'POST', body }),
        enableEmailFactor: () => request<{ success: true }>('/api/mfa/email/enable', { method: 'POST' }),
        verifyEmailFactor: (body: { code: string }) =>
            request<{ success: true }>('/api/mfa/email/verify', { method: 'POST', body }),
        disableEmailFactor: () => request<{ success: true }>('/api/mfa/email/disable', { method: 'POST' }),
        setupTotp: () => request<TotpSetup>('/api/mfa/totp/setup', { method: 'POST' }),
        verifyTotp: (body: { code: string }) =>
            request<{ success: true }>('/api/mfa/totp/verify', { method: 'POST', body }),
        removeTotp: () => request<{ success: true }>('/api/mfa/totp/remove', { method: 'POST' }),
        logout: () => request<{ success: true; logoutUrl: string | null }>('/api/logout', { method: 'POST' }),
    }
}

export type AccountApi = ReturnType<typeof createAccountApi>
